import { useSelector } from 'react-redux';
import { Clock } from 'lucide-react';
import ImageArrayEditorContainer from '../../components/ImageArrayEditorContainer';
import RatingsSection from '../../components/RatingsSection';
import LocationSelector from '../../components/LocationSelector';

// Profile stat keys to display in the stats section
const STAT_KEYS = ['age', 'height', 'method'];

// Just displays profile data
function ProfileCard({ profileData }) {
  const authUserId = useSelector((s) => s.user.authUserId);

  // Extract stats and images from user profile with fallbacks
  const stats = profileData?.stats ?? {};
  const images = profileData?.images ?? [];

  return (
    <div className="profileCard relative">
      {/* Image gallery - view-only */}
      <ImageArrayEditorContainer
        userId={profileData?.id}
        images={images}
        editable={false}
      />

      <div className="space-y-4 px-5 pt-5 pb-24">
        {/* View mode - show name, age, and location as read-only */}
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-3">
            <h2 className="flex-1 truncate text-2xl font-semibold text-foreground">{profileData?.name || 'Unnamed'}</h2>
            <div className="shrink-0 text-2xl font-semibold text-foreground">{stats.age || ''}</div>
          </div>
          <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <LocationSelector location={profileData?.location} editable={false} />
            <div className="flex items-center gap-1 shrink-0">
              <Clock size={14} className="shrink-0" />
              <span>Active recently</span>
            </div>
          </div>
        </div>

        {/* Ratings section - shows user ratings/reviews */}
        <RatingsSection userId={profileData?.id} userName={profileData?.name} />

        {/* About section - bio text */}
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">About</h3>
          <p className="text-sm leading-relaxed text-foreground">{profileData?.bio || '—'}</p>
        </div>

        {/* Role badge */}
        {profileData?.role && (
          <div>
            <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
              profileData.role === 'donor' ? 'bg-blue-500 text-white' : 'bg-purple-500 text-white'
            }`}>
              {profileData.role === 'donor' ? 'Donor' : 'Intended Parent'}
            </span>
          </div>
        )}

        {/* Stats section - displays age, height, method in a grid */}
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Stats</h3>
          <div className="grid grid-cols-2 gap-2">
            {STAT_KEYS.filter((k) => stats[k]).map((k) => (
              <div key={k} className="rounded-lg bg-secondary px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
                <div className="text-sm font-medium text-secondary-foreground">{stats[k]}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileCard;
